import React from 'react';
import { Shield, Lock, Eye, Database, Globe, Mail, ArrowLeft } from 'lucide-react';

interface PrivacyPolicyProps {
  onBack?: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        {onBack && (
          <button
            onClick={onBack}
            className="mb-6 flex items-center gap-2 text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to App
          </button>
        )}

        {/* Header */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500 to-sky-500 rounded-xl shadow-lg mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Privacy Policy</h1>
          <p className="text-gray-600 dark:text-gray-400">Last updated: January 9, 2025</p>
        </div>

        {/* Content */}
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
              <Database className="w-6 h-6 text-cyan-500" />
              Information We Collect
            </h2>
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Account Information</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  When you create an account, we collect your email address, display name, and authentication credentials. 
                  If you use social login (Google), we receive basic profile information from the provider.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Usage Data</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  We automatically collect information about how you use FXPrompt, including prompts generated, 
                  templates used, and feature interactions to improve our service.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">API Keys</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Your API keys (OpenAI, Anthropic, etc.) are stored securely in your browser's local storage 
                  and encrypted in our database if you choose cloud sync. We never access or use your API keys.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
              <Lock className="w-6 h-6 text-cyan-500" />
              How We Protect Your Data
            </h2>
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 space-y-4">
              <ul className="space-y-3 text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>All data transmission is encrypted using SSL/TLS protocols</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Passwords are hashed using industry-standard bcrypt algorithm</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>API keys are encrypted at rest and never exposed to our servers</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Regular security audits and updates to maintain data protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Secure Firebase infrastructure with built-in security rules</span>
                </li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
              <Eye className="w-6 h-6 text-cyan-500" />
              How We Use Your Information
            </h2>
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 space-y-4">
              <ul className="space-y-3 text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>To provide and maintain the FXPrompt service</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>To notify you about changes to our service</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>To provide customer support and respond to inquiries</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>To monitor usage and improve our service</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>To detect and prevent technical issues</span>
                </li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
              <Globe className="w-6 h-6 text-cyan-500" />
              Third-Party Services
            </h2>
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 space-y-4">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                FXPrompt integrates with the following third-party services:
              </p>
              <ul className="space-y-3 text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span><strong>Firebase:</strong> Authentication and data storage</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span><strong>Google OAuth:</strong> Optional social login</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span><strong>Vercel:</strong> Hosting and analytics</span>
                </li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mt-4 text-sm">
                Each service has its own privacy policy. We encourage you to review their policies.
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">Your Rights</h2>
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 space-y-4">
              <p className="text-gray-600 dark:text-gray-400 mb-4">You have the right to:</p>
              <ul className="space-y-3 text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Access your personal data</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Correct inaccurate data</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Request deletion of your data</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Export your data in a portable format</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Withdraw consent at any time</span>
                </li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
              <Mail className="w-6 h-6 text-cyan-500" />
              Contact Us
            </h2>
            <div className="bg-gradient-to-r from-cyan-50 to-sky-50 dark:from-cyan-900/20 dark:to-sky-900/20 rounded-lg p-6 border border-cyan-200 dark:border-cyan-800">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                <li>Email: <a href="mailto:privacy@fxprompt.app" className="text-cyan-600 dark:text-cyan-400 hover:underline">privacy@fxprompt.app</a></li>
                <li>Website: <a href="https://fxprompt.app" className="text-cyan-600 dark:text-cyan-400 hover:underline">fxprompt.app</a></li>
                <li>Company: Flickmax.com</li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;